import {
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import {
  CommonMediaQueries,
  DIALOG_DATA,
  DialogRef,
  IconModule,
  MediaScreenService,
  SelectComponent,
} from '@hglobal/fin-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, tap } from 'rxjs';
import { v4 as uuid } from 'uuid';

import {
  AccountWithAmountItem,
  AccountWithAmountSelectComponent,
  ButtonComponent,
  WalletWithAmountValue,
} from '@iw/common';
import { AccountType, DialogData } from '@iw/core/entities';
import { convertToFromAmount, stringToInteger } from '@iw/core/helpers';
import { IwDialogService, ProfileService } from '@iw/core/services';
import { environment } from '@iw/environment';
import { ShellStore } from '@iw/shell';
import { DictionariesStore } from '@iw/store';

import { WITHDRAWAL_API, WithdrawalApiService } from './api';
import {
  ApmFormComponent,
  WithdrawalDetailsComponent,
  WithdrawalInfoDialogComponent,
} from './components';
import {
  ApmOrderDetails,
  buildFormForMethod,
  getWithdrawalMethodsByCurrency,
  WithdrawalDialogData,
  WithdrawalMethod,
  WithdrawalMethodId,
  WithdrawalReq,
} from './entities';
import { WithdrawalMockApiService } from './mocks';
import { WithdrawalStore } from './withdrawal.store';

@Component({
  selector: 'iw-withdrawal',
  imports: [
    AccountWithAmountSelectComponent,
    ReactiveFormsModule,
    ApmFormComponent,
    SelectComponent,
    TranslateModule,
    ButtonComponent,
    IconModule,
    WithdrawalDetailsComponent,
  ],
  providers: [
    WithdrawalStore,
    {
      provide: WITHDRAWAL_API,
      useClass: environment.useMocks
        ? WithdrawalMockApiService
        : WithdrawalApiService,
    },
  ],
  templateUrl: './withdrawal.component.html',
  styleUrl: './withdrawal.component.scss',
})
export class WithdrawalComponent {
  readonly #shellStore = inject(ShellStore);
  readonly #store = inject(WithdrawalStore);
  readonly #fb = inject(FormBuilder);
  readonly #injector = inject(Injector);
  readonly #translate = inject(TranslateService);
  readonly #dialogService = inject(IwDialogService);
  readonly #mediaScreenService = inject(MediaScreenService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #dialogRef = inject(DialogRef);
  readonly #dialog =
    inject<DialogData<WithdrawalComponent, WithdrawalDialogData>>(DIALOG_DATA);
  readonly #dictStore = inject(DictionariesStore);
  readonly #profileService = inject(ProfileService);

  readonly #requestId = uuid();
  readonly supportLink = environment.supportLink;
  readonly withdrawalMethodIdEnum = WithdrawalMethodId;
  readonly feeLoading = this.#store.isEstimateFeePending;

  withdrawalForm: FormGroup = this.#fb.nonNullable.group({
    walletAndAmountFrom: <WalletWithAmountValue>null,
    walletAndAmountTo: <WalletWithAmountValue>null,
    method: [<WithdrawalMethodId>null, Validators.required],
    apmDetailsForm: this.#fb.group<ApmOrderDetails>({} as ApmOrderDetails),
  });

  accounts = this.dialogData?.accounts || [
    ...this.#shellStore.fiatAccounts(),
    ...this.#shellStore.cryptoAccounts(),
  ];

  withdrawalMethods = this.#store.withdrawalMethods;
  methodsByCurrency = signal<WithdrawalMethod[]>(null);
  isConfirmationStep = signal<boolean>(false);
  withdrawalReq = signal<WithdrawalReq>(null);
  withAmountCtrlError = signal<string | null>(null);

  readonly defaultCurrency = this.#shellStore.defaultCurrency;
  readonly isCodeEnabled = this.#shellStore.isCodeEnabled;
  readonly me = this.#profileService.me;

  readonly disabledMethodIds = computed<WithdrawalMethodId[]>(() =>
    this.isCodeEnabled() ? [] : [WithdrawalMethodId.CODEOffice],
  );

  readonly methodErrorDictionary = computed(() => ({
    cardPersonalUnavailable:
      'withdrawal.dialog.methodSelect.error.cardPersonal',
    paypalUnavailable: 'withdrawal.dialog.methodSelect.error.paypal',
    advcashUnavailable: 'withdrawal.dialog.methodSelect.error.advcash',
    bankTransferINRUnavailable:
      'withdrawal.dialog.methodSelect.error.bankTransferINR',
    CODEOfficeUnavailable: this.#translate.instant(
      'withdrawal.dialog.methodSelect.error.CODEOffice',
    ),
  }));

  readonly availableCurrencies = computed(() => {
    const currencies = this.#dictStore.currencies();
    const currencyIds = this.me()?.location?.availableCurrencies;
    if (!currencies.length || !currencyIds.length) return [];

    return currencies
      .filter((cur) => currencyIds.includes(cur.id))
      .map(
        (currency) =>
          ({
            id: currency.id,
            currency,
            viewType: 'currency',
          }) as AccountWithAmountItem,
      );
  });

  isDesktop = toSignal(
    this.#mediaScreenService.mediaMatcher(CommonMediaQueries.MD),
  );

  get rate(): string | null {
    const from = this.walletAndAmountFromCtrl.value?.account.currency.symbol;
    const to = this.walletAndAmountToCtrl.value?.account.currency.symbol;
    if (!from || !to) return null;

    return this.#dictStore.getRate(from, to) ?? null;
  }

  get dialogData(): WithdrawalDialogData {
    return this.#dialog?.data;
  }

  get walletAndAmountFromCtrl(): AbstractControl<WalletWithAmountValue> {
    return this.withdrawalForm.get('walletAndAmountFrom');
  }

  get walletAndAmountToCtrl(): AbstractControl<WalletWithAmountValue> {
    return this.withdrawalForm.get('walletAndAmountTo');
  }

  get methodCtrl(): AbstractControl {
    return this.withdrawalForm.get('method');
  }

  get apmDetailsForm(): FormGroup {
    return this.withdrawalForm.get('apmDetailsForm') as FormGroup;
  }

  constructor() {
    this.#subscribeOnAccountFromChange();
    this.#subscribeOnAccountToChange();
    this.#subscribeOnMethodChange();
    this.#preselectAccount();
  }

  closeDialog(): void {
    this.#dialogRef.close();
  }

  back(): void {
    this.isConfirmationStep.set(false);
    this.withdrawalForm.enable({ emitEvent: false });
  }

  next(): void {
    if (this.withdrawalForm.invalid) {
      return;
    }

    this.#prepareCreateWithdrawalReq();
    this.withdrawalForm.disable({ emitEvent: false });
    this.isConfirmationStep.set(true);
  }

  confirm(): void {
    this.#store.withdrawal(this.withdrawalReq());
    this.#openInfoDialog();
  }

  #preselectAccount() {
    const isUserHasOnlyOneAccount = this.accounts?.length === 1;
    if (!this.dialogData?.account && !isUserHasOnlyOneAccount) {
      return;
    }

    const account = this.dialogData?.account || this.accounts[0];

    this.walletAndAmountFromCtrl.setValue({ account, amount: null });
  }

  #subscribeOnAccountFromChange(): void {
    this.walletAndAmountFromCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((res) => !!res?.account),
        tap(() => this.#validateAmount()),
        distinctUntilChanged(
          (current: WalletWithAmountValue, prev: WalletWithAmountValue) =>
            current.account?.currency.id === prev.account?.currency.id,
        ),
      )
      .subscribe(({ account }) => {
        const isCrypto = account.accountType === AccountType.CryptoWallet;

        this.#handleMethodField(account.currency.symbol);

        if (isCrypto) {
          this.methodCtrl.setValue(WithdrawalMethodId.Crypto, {
            emitEvent: false,
          });

          this.#buildApmForm();
        }

        this.#validateAmount();
      });
  }

  #subscribeOnAccountToChange(): void {
    this.walletAndAmountToCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((res) => !!res?.account),
      )
      .subscribe(({ amount }) => {
        let nextAmount = amount;

        if (this.rate) {
          nextAmount = convertToFromAmount(amount, this.rate);
        }

        this.walletAndAmountFromCtrl.patchValue(
          {
            account: this.walletAndAmountFromCtrl.value?.account,
            amount: nextAmount,
          },
          { emitEvent: false },
        );

        this.#validateAmount();
      });
  }

  #subscribeOnMethodChange(): void {
    this.methodCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef), filter(Boolean))
      .subscribe((value) => {
        this.#onCODEMethodSelected(value);
        this.#buildApmForm();
        this.#validateAmount();
      });
  }

  #handleMethodField(currency: string): void {
    this.methodCtrl.setErrors(null);
    this.methodsByCurrency.set(
      getWithdrawalMethodsByCurrency(
        this.withdrawalMethods(),
        currency,
        this.disabledMethodIds(),
      ),
    );
    const currentMethodId = this.methodCtrl.value;
    const isCurrentMethodAvailable = !!this.methodsByCurrency().find(
      (method) => method.id === currentMethodId,
    );

    if (!currentMethodId || isCurrentMethodAvailable) {
      return;
    }

    const currentMethod = this.withdrawalMethods().find(
      (method) => method.id === currentMethodId,
    );
    this.methodCtrl.setValue(null);
    this.methodCtrl.setErrors({ [`${currentMethod.name}Unavailable`]: true });
    this.withdrawalForm.updateValueAndValidity();
  }

  #buildApmForm(): void {
    const formByMethod = buildFormForMethod(this.methodCtrl.value, this.#fb);
    this.withdrawalForm.setControl('apmDetailsForm', formByMethod);
    if (this.withdrawalReq()) {
      this.apmDetailsForm.patchValue(this.withdrawalReq().details);
    }

    this.withdrawalForm.updateValueAndValidity();
    this.walletAndAmountToCtrl.setErrors(null);
  }

  #prepareCreateWithdrawalReq(): void {
    const { account, amount } = this.walletAndAmountFromCtrl.value;
    const { account: accountTo, amount: amountTo } =
      this.walletAndAmountToCtrl.value ?? {};

    const targetCurrencyId = accountTo?.id || account.currencyId;
    const isCrypto = account.accountType === AccountType.CryptoWallet;

    const cryptoReq = isCrypto
      ? {
          targetCryptoAddress: this.apmDetailsForm.value.address,
          description: this.apmDetailsForm.value.description,
          details: null as ApmOrderDetails,
          method: null as WithdrawalMethodId,
        }
      : null;

    this.withdrawalReq.set({
      amount: Number(amountTo) || stringToInteger(amount),
      fromAccountId: account.id,
      method: this.methodCtrl.value,
      details: this.apmDetailsForm.value,
      requestId: this.#requestId,
      targetCurrencyId,
      rate: this.rate,
      ...cryptoReq,
    });
  }

  #openInfoDialog(): void {
    this.#dialogService
      .open<WithdrawalInfoDialogComponent, void>(
        {
          components: [WithdrawalInfoDialogComponent],
          isOnTop: true,
        },
        this.#injector,
      )
      .afterClosed<boolean>()
      .pipe(filter(() => this.#store.isWithdrawalSuccess()))
      .subscribe(() => {
        this.#shellStore.handleOperationCompleted(this.#store.operationId());
        this.#dialogRef.close(true);
      });
  }

  #validateAmount(): void {
    const { account: accountFrom, amount: amountFrom } =
      this.walletAndAmountFromCtrl?.value ?? {};
    const { amount: amountTo } = this.walletAndAmountToCtrl?.value ?? {};

    if (this.methodCtrl.value !== WithdrawalMethodId.CODEOffice) {
      this.withAmountCtrlError.set(null);
      return;
    }

    if (Number(amountTo) % 100) {
      this.withAmountCtrlError.set(
        this.#translate.instant('common.validationMessages.multipleOfHundred'),
      );
      return;
    }

    if (Number(amountFrom) > Number(accountFrom?.balance)) {
      this.withAmountCtrlError.set(
        this.#translate.instant('common.validationMessages.insufficientFunds'),
      );
      return;
    }

    this.withAmountCtrlError.set(null);
  }

  #onCODEMethodSelected(method: WithdrawalMethodId): void {
    if (this.walletAndAmountFromCtrl.disabled) {
      this.walletAndAmountFromCtrl.enable();
    }

    if (method === WithdrawalMethodId.CODEOffice) {
      this.walletAndAmountFromCtrl.disable();
      return;
    }

    this.walletAndAmountToCtrl.reset();
  }
}
